import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { QuestionModel } from 'models/generated/Entities.GormCrm.Question';
import { QuestionnaireModel } from 'models/generated/Entities.GormCrm.Questionnaire';
import { QuestionnaireQuestionModel } from 'models/generated/Entities.GormCrm.QuestionnaireQuestion';
import { QuestionnaireQuestionReplyModel } from 'models/generated/Entities.GormCrm.QuestionnaireQuestionReply';
import { QuestionDtoModel } from 'models/generated/Models.QuestionDto';
import { QuestionReplyDtoModel } from 'models/generated/Models.QuestionReplyDto';
import { QuestionnaireDtoModel } from 'models/generated/Models.QuestionnaireDto';
import { QuestionnaireViewModel } from 'models/generated/Models.QuestionnaireView';
import { firstValueFrom } from 'rxjs';
import { LiveDataTracker, TrackingContext } from 'services/data-tracking';
import { PushService } from 'services/push.service';


@Injectable()
export class QuestionnaireService {

	private baseUrl = `${environment.gatewayUrl}/api/questionnaires`;
	private questionnaireTracker = new LiveDataTracker<QuestionnaireModel, { questionnaireId: string }>(questionnaire => questionnaire.id!);
	private allQuestionnairesPromise?: Promise<QuestionnaireModel[]>;

	constructor(private readonly http: HttpClient, private readonly pushService: PushService) {
		this.pushService.onQuestionnaireChange.subscribe(event => {
			this.questionnaireTracker.refreshData(event, event.data.questionnaireId, () => this.getQuestionnaireById(event.data.questionnaireId));
		});
	}

	getAllQuestionnaires(trackingContext?: TrackingContext): Promise<QuestionnaireModel[]> {
		return this.allQuestionnairesPromise = firstValueFrom(this.http.get<QuestionnaireModel[]>(`${this.baseUrl}`))
			.then(questionnaires => this.questionnaireTracker.trackArrayForComponent(trackingContext, questionnaires));
	}

	getQuestionnaireById(id: string, trackingContext?: TrackingContext): Promise<QuestionnaireModel> {
		return firstValueFrom(this.http.get<QuestionnaireModel>(`${this.baseUrl}/${id}`))
			.then(questionnaire => this.questionnaireTracker.trackObjectForComponent(trackingContext, questionnaire));
	}

	async getQuestionnaireByTag(tag: string): Promise<QuestionnaireModel | undefined> {
		if (!this.allQuestionnairesPromise) this.allQuestionnairesPromise = this.getAllQuestionnaires();
		return (await this.allQuestionnairesPromise).find(q => q.tag == tag);
	}

	removeQuestionFromQuestionnaire(questionnaireQuestion: QuestionnaireQuestionModel): Promise<void> {
		return firstValueFrom(this.http.delete<void>(`${this.baseUrl}/${questionnaireQuestion.questionnaireId}/questions/${questionnaireQuestion.questionId}`));
	}

	getQuestionById(id: string): Promise<QuestionModel> {
		return firstValueFrom(this.http.get<QuestionModel>(`${this.baseUrl}/questions/${id}`));
	}

	getQuestions(): Promise<QuestionModel[]> {
		return firstValueFrom(this.http.get<QuestionModel[]>(`${this.baseUrl}/questions`));
	}

	saveQuestion(question: QuestionModel, questionnaireId: string, productIds: string[]): Promise<QuestionModel> {
		question.include(q => q.questionnaireQuestionProducts);
		question.include(q => q.questionReplyOptions);
		question.include(q => q.questionTags);
		const questionDto = new QuestionDtoModel();
		questionDto.question = question;
		questionDto.productIds = productIds;
		questionDto.include(dto => dto.question);
		return firstValueFrom(this.http.post<QuestionModel>(`${this.baseUrl}/${questionnaireId}/questions`, questionDto));
	}

	updateQuestionnaireQuestionRequired(questionnaireId: string, questionId: string, required: boolean): Promise<void> {
		return firstValueFrom(this.http.put<void>(`${this.baseUrl}/${questionnaireId}/questions/${questionId}?required=${required}`, {}));
	}

	addQuestionToQuestionnaire(questionId: string, questionnaireId: string): Promise<void> {
		return firstValueFrom(this.http.post<void>(`${this.baseUrl}/${questionnaireId}/questions/${questionId}`, null));
	}

	updateQuestionReply(questionId: string, replyData: unknown[], companyId: string, orderId?: string, orderLineId?: string, taskflowId?: string): Promise<void> {
		const questionReplyDto = new QuestionReplyDtoModel();
		questionReplyDto.companyId = companyId;
		questionReplyDto.orderId = orderId;
		questionReplyDto.orderLineId = orderLineId;
		questionReplyDto.taskflowId = taskflowId;
		questionReplyDto.replyData = JSON.stringify(replyData);
		return firstValueFrom(this.http.put<void>(`${this.baseUrl}/questions/${questionId}/replies`, questionReplyDto));
	}

	getQuestionnaireView(questionnaireId: string, orderLineIds: string[], taskflowId?: string): Promise<QuestionnaireViewModel> {
		return firstValueFrom(this.http.post<QuestionnaireViewModel>(`${this.baseUrl}/${questionnaireId}/view?taskflowId=${taskflowId}`, orderLineIds));
	}

	getQuestionnaireViewByTag(questionnaireTag: string, orderLineIds: string[], taskflowId?: string): Promise<QuestionnaireViewModel> {
		return firstValueFrom(this.http.post<QuestionnaireViewModel>(`${this.baseUrl}/from-tag/view?tag=${questionnaireTag}&taskflowId=${taskflowId}`, orderLineIds));
	}
	
	getQuestionReply(questionId: string, companyId: string, orderId: string | undefined, orderLineId: string | undefined, taskflowId?: string): Promise<QuestionnaireQuestionReplyModel> {
		const questionReplyDto = new QuestionReplyDtoModel();
		questionReplyDto.companyId = companyId;
		questionReplyDto.orderId = orderId;
		questionReplyDto.orderLineId = orderLineId;
		questionReplyDto.taskflowId = taskflowId;
		return firstValueFrom(this.http.post<QuestionnaireQuestionReplyModel>(`${this.baseUrl}/questions/${questionId}/replies`, questionReplyDto));
	}

	getQuestionRepliesByTag(tag: string, companyId: string, orderId: string | undefined, orderLineId: string | undefined, taskflowId?: string): Promise<QuestionnaireQuestionReplyModel[]> {
		const questionReplyDto = new QuestionReplyDtoModel();
		questionReplyDto.companyId = companyId;
		questionReplyDto.orderId = orderId;
		questionReplyDto.orderLineId = orderLineId;
		questionReplyDto.taskflowId = taskflowId;
		return firstValueFrom(this.http.post<QuestionnaireQuestionReplyModel[]>(`${this.baseUrl}/questions/${tag}/replies`, questionReplyDto));
	}

	updateQuestionnaireName(questionnaireId: string, name: string): Promise<void> {
		const questionnaireDto = new QuestionnaireDtoModel();
		questionnaireDto.name = name;
		return firstValueFrom(this.http.put<void>(`${this.baseUrl}/${questionnaireId}`, questionnaireDto));
	}

	updateQuestionnaireSortOrder(questionnaireQuestions: QuestionnaireQuestionModel[]): Promise<void> {
		return firstValueFrom(this.http.put<void>(`${this.baseUrl}/questions`, questionnaireQuestions));
	}
}
